<template>
  <div class="terms-and-conditions">
    <h2 class="section-title large">{{ $t('terms.title') }}</h2>
    <p class="subtitle">{{ $t('terms.subtitle') }}</p>

    <div class="terms-content">
      <section class="content-section intro-section">
        <div class="section-badge">{{ $t('terms.aboutNumbersBetting') }}</div>
        <p class="intro-text">{{ $t('terms.introText') }}</p>
      </section>

      <section class="content-section">
        <div class="section-badge">{{ $t('terms.availableMarkets') }}</div>
        
        <div class="markets-grid">
          <div v-for="market in markets" :key="market.title" class="market-card">
            <div class="market-card-header">
              <span class="market-icon">{{ market.icon }}</span>
              <h3 class="market-title">{{ $t(market.title) }}</h3>
            </div>
            <div class="market-content">
              <p class="market-name">{{ $t(market.name) }}</p>
              <div class="market-description">
                <span>{{ $t(market.description) }}</span>
                <ul v-if="market.colors">
                  <li v-for="color in colorRules" :key="color.key">
                    <strong>{{ $t(color.key) }}:</strong> {{ color.numbers }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="content-section rules-section">
        <div class="section-badge">{{ $t('terms.importantRules') }}</div>
        
        <div class="rules-list">
          <div v-for="(rule, index) in rules" :key="rule.title" class="rule-card">
            <div class="rule-number">{{ index + 1 }}</div>
            <div class="rule-content">
              <h4 class="rule-title">{{ $t(rule.title) }}</h4>
              <p>{{ $t(rule.text) }}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineOptions } from 'vue'
defineOptions({
  name: 'TermsAndConditionsView'
})

const markets = [
  ['📊', 'sumOddEven'],
  ['⚖️', 'sumOverUnder'],
  ['🎯', 'sumWithinRange'],
  ['⭐', 'bonusBallOddEven'],
  ['✨', 'sumBonusBallsOddEven'],
  ['📈', 'bonusBallOverUnder'],
  ['📉', 'sumBonusBallsOverUnder'],
  ['🔢', 'bonusBallSingleDigit'],
  ['🔗', 'bonusBallMatchesRegular'],
  ['🎲', 'bonusBallWithinRange'],
  ['➕', 'combinedTotalOverUnder'],
  ['🎯', 'standardBetNoBonus'],
  ['⭐', 'standardBetSameDrum'],
  ['🎪', 'standardBetAdditionalDrum'],
  ['🎲', 'bonusBetSameDrum'],
  ['🎰', 'bonusBetDifferentDrum'],
  ['🎯', 'regularPlusBonusSameDrum'],
  ['🎪', 'ballNotToFall'],
  ['📊', 'bonusBallColor', true]
].map(([icon, key, colors]) => ({
  icon,
  title: `terms.${key}`,
  name: `terms.${key}Name`,
  description: `terms.${key}Desc`,
  colors: Boolean(colors)
}))

const colorRules = [
  { key: 'terms.colorGreen', numbers: '(1, 8, 15, 22, 29, 36, 43)' },
  { key: 'terms.colorRed', numbers: '(2, 9, 16, 23, 30, 37, 44)' },
  { key: 'terms.colorOrange', numbers: '(3, 10, 17, 24, 31, 38, 45)' },
  { key: 'terms.colorYellow', numbers: '(4, 11, 18, 25, 32, 39, 46)' },
  { key: 'terms.colorBrown', numbers: '(5, 12, 19, 26, 33, 40, 47)' },
  { key: 'terms.colorPurple', numbers: '(6, 13, 20, 27, 34, 41, 48)' },
  { key: 'terms.colorBlue', numbers: '(7, 14, 21, 28, 35, 42, 49)' }
]

const rules = Array.from({ length: 10 }, (_, index) => ({
  title: `terms.rule${index + 1}Title`,
  text: `terms.rule${index + 1}Text`
}))
</script>

<style scoped>
.terms-and-conditions {
  max-width: 100%;
  margin: 0 auto;
  padding: 0 10px;
}

/* Content Sections */
.terms-content {
  display: flex;
  flex-direction: column;
  gap: 56px;
}

.content-section {
  position: relative;
}

.section-badge {
  display: inline-block;
  background: var(--accent-color);
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 20px;
}

.content-section .section-title {
  font-size: 32px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 24px 0;
}

/* Introduction */
.intro-text {
  font-size: 16px;
  line-height: 1.8;
  color: var(--text-secondary);
  margin: 0;
}

/* Markets Grid */
.markets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  margin-top: 32px;
}

.market-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 24px;
  transition: all 0.3s ease;
  cursor: default;
}

.market-card:hover {
  border-color: var(--accent-color);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.market-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.market-icon {
  font-size: 28px;
  line-height: 1;
}

.market-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.market-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.market-name {
  font-size: 16px;
  font-weight: 500;
  color: var(--accent-color);
  margin: 0;
}

.market-description {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-secondary);
  margin: 0;
}

/* Rules Section */
.rules-section {
  margin-top: 16px;
}

.rules-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 32px;
}

.rule-card {
  display: flex;
  gap: 20px;
  padding: 24px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.rule-card:hover {
  border-color: var(--accent-color);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.rule-number {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-color);
  color: white;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 700;
}

.rule-content {
  flex: 1;
}

.rule-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.rule-content p {
  font-size: 15px;
  line-height: 1.7;
  color: var(--text-secondary);
  margin: 0;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .terms-content {
    gap: 40px;
  }

  .content-section .section-title {
    font-size: 26px;
    margin-bottom: 20px;
  }

  .intro-text {
    font-size: 15px;
  }

  .markets-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 24px;
  }

  .market-card {
    padding: 20px;
  }

  .market-card-header {
    margin-bottom: 12px;
  }

  .market-icon {
    font-size: 24px;
  }

  .market-title {
    font-size: 16px;
  }

  .market-name {
    font-size: 15px;
  }

  .market-description {
    font-size: 13px;
  }

  .rules-list {
    gap: 16px;
    margin-top: 24px;
  }

  .rule-card {
    flex-direction: column;
    gap: 16px;
    padding: 20px;
  }

  .rule-number {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .rule-title {
    font-size: 16px;
  }

  .rule-content p {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .content-section .section-title {
    font-size: 22px;
  }

  .market-card {
    padding: 16px;
  }

  .rule-card {
    padding: 16px;
  }
}
</style>
